import React, { useEffect, useState } from 'react';
import { screeningAPI } from '@/services/api';
import { ClipboardList, Eye, Video, FileText, AlertCircle } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Resolve a recording_url which may be absolute, a /api/files/... path, or a relative path
function resolveMedia(url) {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith('/api/')) return BACKEND + url;
  if (url.startsWith('/')) return BACKEND + url;
  return BACKEND + '/api/files/' + url;
}

function ClipBlock({ title, src, transcript, timestamp, dataTestId }) {
  if (!src && !transcript) return null;
  return (
    <div className="bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800/50 rounded-lg p-3" data-testid={dataTestId}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-slate-600 dark:text-zinc-300 text-xs font-medium flex items-center gap-1.5">
          <Video className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" /> {title}
        </p>
        {timestamp && <span className="text-slate-500 dark:text-zinc-600 text-[10px]">{new Date(timestamp).toLocaleString()}</span>}
      </div>
      <div className="grid md:grid-cols-2 gap-3 items-start">
        {src ? (
          <video src={resolveMedia(src)} controls preload="metadata" className="w-full rounded border border-slate-200 dark:border-zinc-800 bg-black aspect-video" data-testid={`${dataTestId}-video`} />
        ) : (
          <div className="bg-slate-50 dark:bg-zinc-900/40 border border-dashed border-slate-200 dark:border-zinc-800 text-slate-500 dark:text-zinc-600 text-xs rounded aspect-video flex items-center justify-center"><AlertCircle className="w-4 h-4 mr-1.5" /> No recording</div>
        )}
        <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded p-3 max-h-56 overflow-auto">
          <p className="text-slate-500 dark:text-zinc-500 text-[11px] uppercase tracking-wider mb-1.5 flex items-center gap-1"><FileText className="w-3 h-3" /> Transcript</p>
          <p className="text-slate-600 dark:text-zinc-300 text-xs whitespace-pre-wrap leading-relaxed">{transcript || <span className="text-slate-500 dark:text-zinc-600 italic">No transcript available</span>}</p>
        </div>
      </div>
    </div>
  );
}

export default function RecruiterScreenings() {
  const [sessions, setSessions] = useState([]);
  const [detail, setDetail] = useState(null);

  useEffect(() => { screeningAPI.getRecruiterSessions().then(setSessions).catch(() => { /* noop */ }); }, []);

  const viewDetail = async (id) => {
    try { setDetail(await screeningAPI.getSession(id)); } catch (e) { alert(e.message); }
  };

  if (detail) {
    const ev = detail.overall_evaluation;
    const warmupUrl = detail.warmup_recording_url || detail.warmup_url;
    const warmupText = detail.warmup_transcript || detail.warmup_text;
    return (
      <div data-testid="screening-detail">
        <button onClick={() => setDetail(null)} className="text-slate-500 dark:text-zinc-500 text-sm mb-4 hover:text-slate-600 dark:hover:text-zinc-300">Back to list</button>
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-white mb-1">Screening Detail</h1>
        <p className="text-slate-500 dark:text-zinc-500 text-sm mb-6">{detail.job_title} &middot; <span className="capitalize">{detail.status}</span></p>
        {ev && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            {[['Overall', ev.overall_score], ['Functional', ev.functional_score], ['Technical', ev.technical_score], ['Behavioral', ev.behavioral_score]].map(([l, v]) => (
              <div key={l} className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-lg p-4 text-center">
                <p className="text-slate-500 dark:text-zinc-500 text-xs mb-1">{l}</p>
                <p className={`text-2xl font-bold ${v >= 70 ? 'text-emerald-600 dark:text-emerald-400' : v >= 50 ? 'text-amber-600 dark:text-amber-400' : 'text-red-600 dark:text-red-400'}`}>{v}</p>
              </div>
            ))}
          </div>
        )}
        {ev?.summary && <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-lg p-4 mb-6"><p className="text-slate-600 dark:text-zinc-300 text-sm">{ev.summary}</p>{ev.recommendation && <p className="text-slate-500 dark:text-zinc-500 text-xs mt-2 capitalize">Recommendation: {ev.recommendation}</p>}</div>}

        {(warmupUrl || warmupText) && (
          <div className="mb-5">
            <p className="text-slate-500 dark:text-zinc-400 text-xs uppercase tracking-wider mb-2">Warm-up · Tell me about yourself</p>
            <ClipBlock title="Warm-up recording" src={warmupUrl} transcript={warmupText} dataTestId="warmup-clip" />
          </div>
        )}

        <div className="space-y-3">
          {detail.questions?.map((q, i) => (
            <div key={i} className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-lg p-4" data-testid={`q-block-${i}`}>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs px-2 py-0.5 rounded bg-slate-100 dark:bg-zinc-800 text-slate-500 dark:text-zinc-400 inline-block">{q.category}</span>
                {q.evaluation?.score !== undefined && (
                  <span className={`text-xs font-medium ${q.evaluation.score >= 70 ? 'text-emerald-600 dark:text-emerald-400' : q.evaluation.score >= 50 ? 'text-amber-600 dark:text-amber-400' : 'text-red-600 dark:text-red-400'}`}>Score: {q.evaluation.score}/100</span>
                )}
              </div>
              <p className="text-slate-900 dark:text-white text-sm mb-3">Q{i + 1}. {q.question}</p>
              <ClipBlock
                title={`Answer ${i + 1}`}
                src={q.recording_url || q.answer_url || q.audio_url}
                transcript={q.answer_text}
                timestamp={q.answered_at}
                dataTestId={`answer-${i}`}
              />
              {q.evaluation?.feedback && <p className="text-slate-500 dark:text-zinc-500 text-xs mt-2 italic">Feedback: {q.evaluation.feedback}</p>}
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="recruiter-screenings">
      <h1 className="text-2xl font-semibold text-slate-900 dark:text-white mb-1">Screenings</h1>
      <p className="text-slate-500 dark:text-zinc-500 text-sm mb-6">AI screening sessions</p>
      {sessions.length === 0 ? (
        <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-lg p-10 text-center"><ClipboardList className="w-10 h-10 text-slate-600 dark:text-zinc-700 mx-auto mb-3" /><p className="text-slate-500 dark:text-zinc-500">No screening sessions</p></div>
      ) : (
        <div className="space-y-3">
          {sessions.map(s => (
            <div key={s.id} className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-lg p-4 flex items-center justify-between">
              <div>
                <p className="text-slate-900 dark:text-white font-medium">{s.job_title || 'Screening'}</p>
                <p className="text-slate-500 dark:text-zinc-500 text-xs capitalize">{s.status.replace('_', ' ')} &middot; {s.questions?.length} questions</p>
              </div>
              <div className="flex items-center gap-3">
                {s.overall_evaluation && <span className="text-emerald-600 dark:text-emerald-400 text-sm font-medium">{s.overall_evaluation.overall_score}/100</span>}
                <button onClick={() => viewDetail(s.id)} className="p-2 text-slate-500 dark:text-zinc-500 hover:text-slate-900 dark:hover:text-white" data-testid={`view-screening-${s.id}`}><Eye className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
